<script setup lang="ts">
import BamboraCardForm from '@/components/Bambora/BamboraCardForm.vue'
import InvoicePaidDetails from '@/components/Invoice/InvoicePaidDetails.vue'
import InvoicePaymentOption from '@/components/Invoice/InvoicePaymentOption.vue'
import OrderDetails from '@/components/OrderDetails.vue'
import PaymentButton from '@/components/PaymentMethodButton.vue'
import PendingTransactionTab from '@/components/PendingTransactionTab.vue'
import StripeCardForm from '@/components/Stripe/StripeCardForm.vue'
import StripeCardPaid from '@/components/Stripe/StripeCardPaid.vue'
import StripeDirectPaymentButton from '@/components/Stripe/StripeDirectPaymentButton.vue'
import VippsButtonLogo from '@/components/SwedbankPay/VippsButtonLogo.vue'
import VippsForm from '@/components/SwedbankPay/VippsForm.vue'
import VippsLogoSmall from '@/components/SwedbankPay/VippsLogoSmall.vue'
import { usePejer } from '@/Pejer'
import { AvailablePaymentMethods, VerifyPaymentStatus } from '@/Pejer/types/checkout'
import { InvoiceCreationResponse } from '@/Pejer/types/invoice'
import { Stripe } from '@stripe/stripe-js/dist/stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import {
  CircleCheckBig,
  CreditCard,
  ExternalLink,
  FileText,
  Landmark,
  LockKeyhole,
  Smile,
} from 'lucide-vue-next'
import DataStateSkeleton from 'pejer/src/components/DataState/DataStateSkeleton.vue'
import HorizontalLineBesideText from 'pejer/src/components/HorizontalLineBesideText.vue'
import { Badge } from 'pejer/src/components/ui/badge'
import Button from 'pejer/src/components/ui/button/Button.vue'
import { toast } from 'pejer/src/components/ui/toast'
import ViewSwipe, { ViewSwipePages } from 'pejer/src/components/ViewPicker/ViewSwipe.vue'
import { HTTP_STATUS_CODES } from 'pejer/src/Consts'
import usePejerQuery from 'pejer/src/usePejerQuery'
import { FunctionalComponent, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const paymentReferenceID = route.params.paymentReferenceID as string
const alreadyFinishedPaymentFlow = ref(false)

const pejer = usePejer()
const { data, dataState, error, isError, isLoading } = usePejerQuery(
  pejer.urls.checkout.getPayment,
  {
    queryKey: [],
    pathArgs: {
      paymentReferenceID,
    },
    retry: false,
  },
  {
    ignoreAuthorization: true,
  }
)

const pageSwipePages = [
  {
    id: 'card',
    icon: CreditCard,
    label: 'Kortbetaling',
    skipSlideAnimation: false as boolean,
  },
  // {
  //   id: 'card-swedbankpay',
  //   icon: CreditCard,
  //   label: 'Kortbetaling',
  // },
  {
    id: 'applepay-swedbankpay',
    icon: CreditCard,
    label: 'Apple Pay',
    skipSlideAnimation: false as boolean,
  },
  {
    id: 'googlepay-swedbankpay',
    icon: CreditCard,
    label: 'Google Pay',
    skipSlideAnimation: false as boolean,
  },
  {
    id: 'invoice',
    icon: FileText,
    label: 'Fakturabetaling',
    skipSlideAnimation: false as boolean,
  },
  {
    id: 'vipps',
    icon: VippsLogoSmall as unknown as FunctionalComponent<any>,
    label: 'Vipps',
    skipSlideAnimation: false as boolean,
  },
  {
    id: 'bank2bank',
    icon: Landmark,
    label: 'Direktebetaling med BankID',
    skipSlideAnimation: false as boolean,
  },
  {
    id: 'invoice-paid',
    hideHeader: true,
    skipSlideAnimation: false as boolean,
  },
  {
    id: 'card-paid',
    hideHeader: true,
    skipSlideAnimation: false as boolean,
  },
  {
    id: 'verifying-payment',
    hideHeader: true,
    skipSlideAnimation: false as boolean,
  },
] as const satisfies ViewSwipePages[]

type PageIds = (typeof pageSwipePages)[number]['id']

const currentPageView = ref<PageIds | null>((route.query.view as PageIds) || null)

const paymentMethodToPageView = (method: AvailablePaymentMethods): PageIds | null => {
  switch (method) {
    case AvailablePaymentMethods.Vipps:
      return 'vipps'
    case AvailablePaymentMethods.CreditCard:
      return 'card'
    case AvailablePaymentMethods.Invoice:
      return 'invoice'
    default:
      return null
  }
}

watch(data, (newValue, oldValue) => {
  if (!oldValue && !!newValue && data.value?.paymentMethods.length === 1) {
    const pageId = paymentMethodToPageView(data.value?.paymentMethods[0])
    const page = pageSwipePages.find((page) => page.id === pageId)
    if (page) (page as any).isOnlyOption = true
    currentPageView.value = paymentMethodToPageView(data.value?.paymentMethods[0])
  }
})

const stripe = ref<Stripe | null>(null)

watch(isError, (newValue) => {
  if (newValue && error.value?.statusCode === HTTP_STATUS_CODES.CONFLICT) {
    if (currentPageView.value === 'vipps' || currentPageView.value === 'verifying-payment') {
      ;(async () => {
        const res = await pejer.fetch(pejer.urls.checkout.verifyPayment, {
          pathArgs: {
            paymentReferenceID,
          },
          ignoreAuthorization: true,
        })

        if ('error' in res === false && res.data.status !== VerifyPaymentStatus.Pending) {
          // Error handling
          window.location.href = res.data.redirectUrl!
          return
        }
      })()
    }
    alreadyFinishedPaymentFlow.value = true

    // router.push({
    //   name: 'payment-invoice',
    //   params: {
    //     paymentReferenceID,
    //   },
    // })
  }
})

watch(
  () => data.value?.stripe?.publishableKey,
  async (newKey) => {
    if (!newKey) {
      stripe.value = null
      return
    }
    stripe.value = await loadStripe(newKey)
  },
  {
    immediate: true,
  }
)
const invoicePaidResponse = ref<InvoiceCreationResponse | null>(null)

const paymentMethodAvailable = (method: AvailablePaymentMethods) => {
  return data.value?.paymentMethods.includes(method)
}

const redirect = (url: string | null, fallbackView: PageIds | null) => {
  if (url) window.location.href = url
  else currentPageView.value = fallbackView
}

const paymentVerifiedSuccessfully = ref<boolean | null>(null)
const maxVerifyAttempts = 20
let verifyAttemptsLeft = maxVerifyAttempts

const verifyPaymentState = async (fallbackView: PageIds | null) => {
  if (currentPageView.value !== 'verifying-payment') currentPageView.value = 'verifying-payment'

  if (verifyAttemptsLeft <= 0) {
    // Max attempts reached, redirect to fallback view
    currentPageView.value = fallbackView
    toast({
      title: 'Kunne ikke verifisere betaling, vennligst prøv igjen.',
      variant: 'destructive',
    })
    verifyAttemptsLeft = maxVerifyAttempts
    return
  }

  const res = await pejer.fetch(pejer.urls.checkout.verifyPayment, {
    pathArgs: {
      paymentReferenceID,
    },
    ignoreAuthorization: true,
  })
  verifyAttemptsLeft--

  if ('error' in res) {
    // Error handling
    return
  }

  if (res.data.status === VerifyPaymentStatus.Pending) {
    // Check again after 2 seconds
    setTimeout(() => verifyPaymentState(fallbackView), 2000)
    return
  }

  if (res.data.status === VerifyPaymentStatus.Success) {
    paymentVerifiedSuccessfully.value = true
  } else if (res.data.status === VerifyPaymentStatus.Failed) {
    paymentVerifiedSuccessfully.value = false
  }
  redirect(res.data.redirectUrl!, fallbackView!)
}

if (currentPageView.value === 'verifying-payment') {
  verifyPaymentState(null)
}

const swedbankPayObject = ref<{
  currentPaymentMethod: AvailablePaymentMethods | null
  checkoutInstance: {
    open: () => void
    close: () => void
    refresh: () => void
    resume: () => void
    update: () => void
  } | null
  abortController: AbortController | null
  checkoutScriptID: string
}>({
  currentPaymentMethod: null,
  checkoutInstance: null,
  abortController: null,
  checkoutScriptID: 'payex-checkout-script',
})

const loadedScripts = ref<Set<string>>(new Set())

async function loadScript(src: string, id: string) {
  if (loadedScripts.value.has(id)) return Promise.resolve(document.getElementById(id))

  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.type = 'text/javascript'
    script.async = true // Non-blocking download [29]
    script.id = id

    script.onload = () => {
      console.log('Swedbank Pay helper script loaded')
      resolve(script)
    }
    script.onerror = () => reject(new Error(`Script load error for ${src}`))

    document.body.insertAdjacentElement('afterbegin', script)
    loadedScripts.value.add(id)
  })
}

const switchSwedbankPayCheckoutMethod = async (
  paymentMethod: AvailablePaymentMethods,
  containerId: string,
  events: {
    onError?: (data: any) => void
    onEventNotification?: (data: any) => void
    onPaid?: (data: any) => void
  } = {}
) => {
  swedbankPayObject.value.abortController?.abort() // Abort any ongoing fetch
  swedbankPayObject.value.abortController = new AbortController() // Create a new controller for the new fetch
  const res = await pejer.fetch(pejer.urls.checkout.updateSwedbankPayInstrument, {
    pathArgs: {
      paymentReferenceID: paymentReferenceID,
    },
    body: {
      paymentMethod: paymentMethod,
    },
    abortSignal: swedbankPayObject.value.abortController?.signal,
    ignoreAuthorization: true,
  })

  if ('error' in res) {
    console.error('Error fetching Swedbank Pay script URL:', res.error)
    return null
  }

  swedbankPayObject.value.currentPaymentMethod = paymentMethod

  if (!swedbankPayObject.value.checkoutInstance)
    await loadScript(res.data.scriptUrl, swedbankPayObject.value.checkoutScriptID)
  else swedbankPayObject.value.checkoutInstance.close() // Close existing instance before creating a new one

  const payex = (window as any).payex.hostedView.checkout({
    // The container is the ID of the HTML element you want to place
    // our solution inside of.
    container: {
      checkout: containerId,
    },
    culture: 'nb-NO',
    // This is where you can add your own seamless events.
    // See the section "Events" down below for more information.
    // onError: (Function = (data) => console.error('onError', data)),
    // onEventNotification: (Function = (data) => console.log('onEventNotification', data)),
    // onPaid: (Function = (data) => console.log('onPaid', data)),

    ...events,
  })

  swedbankPayObject.value.checkoutInstance = payex
  swedbankPayObject.value.checkoutInstance!.open()
}

const getAcceptedWallets = async (
  tries: number = 0
): Promise<('GooglePay' | 'ApplePay' | 'ClickToPay')[]> => {
  await loadScript(
    'https://ecom.payex.com/checkout/core/integration',
    'payex-checkout-script-helpers'
  )

  console.log('Checking for Swedbank Pay accepted wallets function, attempt', tries + 1)

  if ((window as any).payex?.getAcceptedWallets) {
    const wallets = (await (window as any).payex.getAcceptedWallets()) as string[]
    if (wallets.length === 0 && tries < 10) {
      // Sometimes the function is available but returns an empty array initially, retry after a delay
      await new Promise((resolve) => setTimeout(resolve, 500))
      return getAcceptedWallets(tries + 1)
    }
    return wallets as ('GooglePay' | 'ApplePay' | 'ClickToPay')[]
  }
  console.warn(
    'Swedbank Pay getAcceptedWallets function not available',
    (window as any).payex,
    (window as any).payex?.getAcceptedWallets
  )
  return []
}

onUnmounted(() => {
  for (const id of loadedScripts.value) {
    const existingScript = document.getElementById(id)
    if (existingScript) document.body.removeChild(existingScript)
  }
})

const swedbankGooglePayEnabled = ref(false)
const swedbankApplePayEnabled = ref(false)
const swedbankPayAcceptedWallets = ref<('GooglePay' | 'ApplePay' | 'ClickToPay')[]>([])

onMounted(async () => {
  swedbankPayAcceptedWallets.value = await getAcceptedWallets()
  console.log('Swedbank Pay helper script should be loaded', swedbankPayAcceptedWallets.value)
})

watch(
  () => [data.value, swedbankPayAcceptedWallets.value],
  async (newData) => {
    const newDataFetched = newData[0] as typeof data.value
    if (!newDataFetched) return

    const isApplePayEnabled =
      newDataFetched.paymentMethodProviders.ApplePay === 'SwedbankPay' &&
      newDataFetched.paymentMethods.includes(AvailablePaymentMethods.ApplePay) &&
      swedbankPayAcceptedWallets.value.includes('ApplePay')

    swedbankApplePayEnabled.value = isApplePayEnabled ?? false

    const isGooglePayEnabled =
      newDataFetched.paymentMethodProviders.GooglePay === 'SwedbankPay' &&
      newDataFetched.paymentMethods.includes(AvailablePaymentMethods.GooglePay) &&
      swedbankPayAcceptedWallets.value.includes('GooglePay')

    swedbankGooglePayEnabled.value = isGooglePayEnabled ?? false
  }
)
</script>
<template>
  <template v-if="!alreadyFinishedPaymentFlow">
    <!-- <div v-if="isError" class="p-12 min-w-96 bg-white">
      <DataStateError :error="error" class="w-full max-w-sm h-fit" />
    </div> -->
    <!-- v-else -->
    <div
      class="w-full relative flex md:flex-row flex-col-reverse min-h-dvh md:justify-start justify-end items-center"
    >
      <!-- <div
        class="w-full top-0 h-1/2 md:w-1/2 md:left-1/2 md:h-full bg-slate-100 absolute -z-50 border-b md:border-l border-slate-200"
      ></div> -->
      <Badge
        v-if="data?.environment === 'test'"
        variant="warning"
        class="absolute top-0 left-1/2 -translate-x-1/2 mt-2 mr-2 p-1 px-2 text-xs z-50"
      >
        Testmiljø
      </Badge>
      <div
        class="flex shadow-lg shadow-merino-200 z-20 flex-col gap-4 w-full md:min-w-[50vw] md:h-dvh min-h-[550px] py-10 justify-start items-center md:justify-center md:items-end border-merino-200 border-t md:border-r"
      >
        <!-- these weird max-w-values is offset by the ViewSwipes padding, this makes the sliding view not so tight -->
        <!-- 'max-w-[532px] md:max-w-[548px] lg:max-w-[580px]' corresponding to 'px-4 md:px-6 lg:px-10' respectively -->
        <div
          class="min-h-[550px] max-w-[532px] md:max-w-[548px] lg:max-w-[580px] w-full justify-start flex flex-col items-end"
        >
          <div class="flex flex-col items-center w-full justify-center text-slate-700 pb-4">
            <p class="text-xs text-slate-700">&nbsp;</p>
            <h1 class="text-xl font-bold">Oppsummering og betaling</h1>
          </div>
          <ViewSwipe
            class="px-4 md:px-6 lg:px-10"
            backLabel="Endre betalingsmetode"
            :views="pageSwipePages"
            :currentView="currentPageView"
            @update:currentView="(currentPageView = $event as PageIds)!"
            :hide-back-label="!data?.paymentMethods || data.paymentMethods.length <= 1"
          >
            <!-- :skip-slide-animation="
              data?.paymentMethods &&
              data.paymentMethods.length <= 1 &&
              pageSwipePages.some((page) => page.id === currentPageView && page.skipSlideAnimation)
            " -->
            <template #card>
              <StripeCardForm
                v-if="data && stripe && data.paymentMethodProviders.CreditCard === 'Stripe'"
                :stripe="stripe"
                :clientSecret="data.stripe.clientSecret"
                :paymentReferenceID="paymentReferenceID"
                :grossAmount="data.grossAmount"
                :currency="data.stripe.currency"
                :customer="{
                  name: data.customer.name,
                  email: data.customer.email,
                  mobile: data.customer.mobile,
                }"
                :success-url="data.successUrl"
                :failed-url="data.failedUrl"
                @onPaidSuccess="
                  (intent) => {
                    verifyPaymentState('card-paid')
                  }
                "
              />
              <VippsForm
                v-else-if="data && data.paymentMethodProviders.CreditCard === 'SwedbankPay'"
                :paymentReferenceID="paymentReferenceID"
                :payment-method="AvailablePaymentMethods.CreditCard"
                :switchSwedbankPayCheckoutMethod="switchSwedbankPayCheckoutMethod"
              />
              <BamboraCardForm
                v-else-if="data && data.paymentMethodProviders.CreditCard === 'Bambora'"
                :sessionToken="data.bambora!.sessionToken"
                :paymentReferenceID="paymentReferenceID"
                @onPaidSuccess="
                  (intent) => {
                    verifyPaymentState('card-paid')
                  }
                "
              />
            </template>
            <template #card-paid>
              <StripeCardPaid />
            </template>
            <template #invoice>
              <InvoicePaymentOption
                v-if="data"
                :paymentReferenceID="paymentReferenceID"
                :currency="data.stripe.currency"
                :grossAmount="data.grossAmount"
                :customer="data.customer"
                @onInvoicePaid="
                  (invoiceCreationResponse) => {
                    invoicePaidResponse = invoiceCreationResponse
                    redirect(invoiceCreationResponse.redirectUrl, 'invoice-paid')
                  }
                "
              />
            </template>
            <template #vipps>
              <VippsForm
                v-if="data"
                :paymentReferenceID="paymentReferenceID"
                :payment-method="AvailablePaymentMethods.Vipps"
                :switchSwedbankPayCheckoutMethod="switchSwedbankPayCheckoutMethod"
              />
            </template>
            <template #applepay-swedbankpay>
              <VippsForm
                v-if="data"
                :paymentReferenceID="paymentReferenceID"
                :payment-method="AvailablePaymentMethods.ApplePay"
                :switchSwedbankPayCheckoutMethod="switchSwedbankPayCheckoutMethod"
              />
            </template>
            <template #googlepay-swedbankpay>
              <VippsForm
                v-if="data"
                :paymentReferenceID="paymentReferenceID"
                :payment-method="AvailablePaymentMethods.GooglePay"
                :switchSwedbankPayCheckoutMethod="switchSwedbankPayCheckoutMethod"
              />
            </template>
            <template #invoice-paid>
              <InvoicePaidDetails :invoiceDetails="invoicePaidResponse!.invoiceDetails!" />
            </template>
            <template #bank2bank>
              <div class="flex flex-col gap-2 w-full h-full">
                <h2 class="text-lg font-semibold">Direktebetaling med BankID</h2>
                <p class="text-sm text-slate-500">
                  Du vil bli videresendt til din banks nettside for å fullføre betalingen.
                </p>
                <p class="text-sm text-slate-500">
                  Vennligst følg instruksjonene på skjermen for å fullføre betalingen.
                </p>
              </div>
            </template>

            <template #verifying-payment>
              <PendingTransactionTab />
            </template>

            <!-- The default view (null) -->
            <HorizontalLineBesideText class="w-full border-slate-200/80 mb-3 flex">
              <span class="text-sm">Velg betalingsmetode</span>
            </HorizontalLineBesideText>
            <div class="grid grid-cols-1 gap-3 w-full mb-2">
              <DataStateSkeleton :data-state="dataState" sizing-class="h-[50px] w-full">
                <PaymentButton
                  v-if="paymentMethodAvailable(AvailablePaymentMethods.CreditCard)"
                  @onClick="currentPageView = 'card'"
                  :icon="CreditCard"
                  :payment-method="AvailablePaymentMethods.CreditCard"
                >
                  Betal med kort
                </PaymentButton>
              </DataStateSkeleton>
              <DataStateSkeleton :data-state="dataState" sizing-class="h-[50px] w-full">
                <PaymentButton
                  v-if="paymentMethodAvailable(AvailablePaymentMethods.Invoice) || isLoading"
                  :payment-method="AvailablePaymentMethods.Invoice"
                  @onClick="currentPageView = 'invoice'"
                  :icon="FileText"
                >
                  Betal med faktura
                </PaymentButton>
              </DataStateSkeleton>
              <DataStateSkeleton :data-state="dataState" sizing-class="h-[50px] w-full">
                <PaymentButton
                  v-if="data?.paymentMethods.includes(AvailablePaymentMethods.Vipps) && !isLoading"
                  @onClick="currentPageView = 'vipps'"
                  :icon="Smile"
                  :payment-method="AvailablePaymentMethods.Vipps"
                >
                  <template #asChild>
                    <!-- <svg
                      class="absolute top-0 left-0 col-span-1 h-full"
                      xmlns="http://www.w3.org/2000/svg"
                      id="Artwork"
                      viewBox="0 0 138.67 104"
                    >
                      <path
                        fill="#ffffff"
                        d="M81.22,40.59c4.23,0,7.86-3.21,7.86-7.84h0c0-4.62-3.63-7.84-7.86-7.84s-7.86,3.21-7.86,7.84,3.63,7.84,7.86,7.84ZM91.5,53.23c-5.24,6.83-10.79,11.55-20.57,11.55h0c-9.98,0-17.74-6.03-23.79-14.87-2.42-3.62-6.15-4.42-8.87-2.51-2.52,1.81-3.12,5.63-.81,8.94,8.37,12.76,19.96,20.19,33.47,20.19,12.4,0,22.08-6.03,29.64-16.07,2.82-3.72,2.72-7.53,0-9.64-2.52-2.01-6.25-1.3-9.07,2.41Z"
                      />
                    </svg> -->
                    <VippsButtonLogo class="h-full w-full col-span-full" />
                  </template>
                </PaymentButton>
              </DataStateSkeleton>
              <template v-if="!isLoading && !isError">
                <StripeDirectPaymentButton
                  v-if="
                    data?.stripe &&
                    paymentMethodAvailable(AvailablePaymentMethods.CreditCard) &&
                    data.paymentMethodProviders.CreditCard === 'Stripe'
                  "
                  :stripe="stripe"
                  :clientSecret="data.stripe?.clientSecret"
                  :paymentReferenceID="paymentReferenceID"
                  :grossAmount="data.grossAmount"
                  :currency="data.stripe?.currency"
                  :googlePayEnabled="
                    paymentMethodAvailable(AvailablePaymentMethods.CreditCard) ?? true
                  "
                  :applePayEnabled="
                    paymentMethodAvailable(AvailablePaymentMethods.CreditCard) ?? true
                  "
                  @onPaidSuccess="
                    (intent) => {
                      verifyPaymentState('card-paid')
                    }
                  "
                />

                <!-- v-if="paymentMethodAvailable(AvailablePaymentMethods.Vipps) || isLoading" -->

                <PaymentButton
                  v-if="paymentMethodAvailable(AvailablePaymentMethods.Bank2Bank) || isLoading"
                  @onClick="currentPageView = 'bank2bank'"
                  :icon="Landmark"
                  :payment-method="AvailablePaymentMethods.Bank2Bank"
                >
                  Direktebetaling med BankID
                </PaymentButton>
                <!-- <PaymentButton
                  v-if="swedbankGooglePayEnabled || isLoading"
                  @onClick="currentPageView = 'googlepay-swedbankpay'"
                  :icon="Landmark"
                  :payment-method="AvailablePaymentMethods.GooglePay"
                >
                  Goggle Pay via Swedbank Pay
                </PaymentButton>
                {{ swedbankGooglePayEnabled }}
                {{ swedbankApplePayEnabled }}
                <PaymentButton
                  v-if="swedbankApplePayEnabled || isLoading"
                  @onClick="currentPageView = 'applepay-swedbankpay'"
                  :icon="Landmark"
                  :payment-method="AvailablePaymentMethods.ApplePay"
                >
                  Aplle Pay via Swedbank Pay
                </PaymentButton> -->
              </template>
            </div>
            <!-- </DataStateDisplay> -->
            <span class="flex items-end gap-2 text-xs text-slate-500 mt-auto mb-1">
              <LockKeyhole :size="20" class="text-green-500" />
              <span class="font-medium">Sikker betaling</span>
            </span>
          </ViewSwipe>
        </div>
      </div>
      <div
        class="flex w-full flex-col gap-4 md:h-dvh h-[50dvh] min-h-fit bg-merino-100 shadow-inner py-10 px-4 md:px-6 lg:px-10 justify-end md:justify-center items-center md:items-start"
      >
        <div class="md:min-h-[550px] max-w-[500px] w-full">
          <OrderDetails
            :orderDetails="data?.orderDetails"
            :currency="data?.currency"
            :description="data?.description"
            :dataState="dataState"
            :orderTotal="data?.grossAmount"
          />
        </div>
      </div>
    </div>
    <template v-if="isError">
      <div class="w-screen h-screen backdrop-blur-sm absolute top-0 left-0 z-50"></div>
      <div
        class="bg-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-8 z-50 rounded-lg shadow-sm border border-slate-200 w-full max-w-sm text-red-500"
      >
        <h1 class="font-semibold">En feil oppstod:</h1>
        <p>
          {{ error!.error }}
        </p>
      </div>
    </template>
  </template>
  <div v-else class="bg-merino-100 w-full min-h-dvh flex justify-center items-center p-4">
    <div
      class="h-fit flex flex-col w-fit py-4 px-4 sm:py-10 sm:px-12 bg-white border-slate-200 border rounded-md shadow-lg shadow-merino-200"
    >
      <CircleCheckBig class="w-20 h-20 text-green-500 self-center mb-4" stroke-width="1.5" />
      <h1 class="text-xl font-semibold w-full text-center">Betalingsflyt fullført</h1>
      <p class="text-sm text-slate-500">
        Du har fullført betalingsflyten. Vennligst sjekk e-posten din for mer informasjon.
      </p>
      <RouterLink
        v-if="error?.problemDetails?.invalidState?.find((e) => e.value === 'InvoiceConfirmed')"
        :to="`/${paymentReferenceID}/invoice`"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button variant="secondary" class="w-full mt-2 bg-slate-100">
          Se faktura
          <ExternalLink class="w-4 h-4 ml-2" stroke-width="1.5" />
        </Button>
      </RouterLink>
      <!-- <HorizontalLineBesideText class="w-full border-slate-200/80 mt-6 italic">
      Andre betalingsmetoder
    </HorizontalLineBesideText>
    <p class="text-sm text-slate-500 italic">Se eposten din for informasjon om betalingen din</p> -->
    </div>
  </div>
</template>
